import React from 'react'
import { useUser } from '@features/auth/hooks'
import { useEventGetUserMatchesQuery } from '@app/services'
import {
  BriefPublicUserProfile,
  useAuthGetAvoidedUsersQuery,
  useAuthGetFolloweesQuery,
  useAuthGetFollowersQuery,
} from '@app/services/auto-generated/auth'
import { UserMatchesResponse } from '@app/services/auto-generated/event'
import {
  Alert,
  Avatar,
  Box,
  CardActionArea,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@common/components'
import { COMMUNITY_ROUTE } from '@common/constants/routes'
import errorHandler from '@common/utils/error-handler'
import { useLocation, useNavigate } from '@lib/router'
import { CardWrapper } from './community-view.styes'

const CommunityView = () => {
  const isPremium = true
  const { user } = useUser()
  const [value, setValue] = React.useState(0)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const fetchData = () => {
    switch (value) {
      case 0:
        return useEventGetUserMatchesQuery()
      case 1:
        return useAuthGetFolloweesQuery({ userId: user?.id as number })
      case 2:
        return useAuthGetFollowersQuery({ userId: user?.id as number })
      case 3:
        return useAuthGetAvoidedUsersQuery()
      default:
        return null
    }
  }

  const dataQuery = fetchData()

  React.useEffect(() => {
    if (value !== 0 && Array.isArray(dataQuery?.data) && dataQuery?.data.length > 0) {
      const firstUser = dataQuery.data[0]?.id
      navigate(`${COMMUNITY_ROUTE}/${firstUser}`)
    } else if (
      value === 0 &&
      dataQuery?.data &&
      'matches' in dataQuery.data &&
      Array.isArray(dataQuery.data.matches) &&
      dataQuery.data.matches.length > 0
    ) {
      const matchesData = dataQuery.data as UserMatchesResponse
      const firstUser = matchesData.matches[0].user_matched_with?.id
      navigate(`${COMMUNITY_ROUTE}/${firstUser}`)
    } else {
      const pathWithoutUserId = pathname
        .split('/')
        .filter((segment) => !/^\d+$/.test(segment))
        .join('/')
      navigate(pathWithoutUserId)
    }
  }, [value, dataQuery])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  return (
    <Stack sx={{ height: '100%', width: '100%' }}>
      <Tabs
        sx={{
          '& .MuiTabScrollButton-root.Mui-disabled': {
            display: 'none',
          },
        }}
        value={value}
        onChange={handleChange}
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile
        aria-label='scrollable auto tabs example'
      >
        <Tab label='Matches' />
        <Tab label='Following' />
        <Tab label='Followers' />
        <Tab label='Avoiding' />
      </Tabs>
      <Box sx={{ pt: 3, flex: 1 }}>
        <Box sx={{ height: '100%' }}>
          {value === 0 && <Matches />}
          {value === 1 && (
            <UserList
              queryHook={useAuthGetFolloweesQuery}
              emptyMessage='You are not following anyone.'
            />
          )}
          {value === 2 &&
            (isPremium ? (
              <UserList
                queryHook={useAuthGetFollowersQuery}
                emptyMessage='You are not being followed by anyone.'
              />
            ) : (
              <p>Upgrade your account to see who follows you.</p>
            ))}

          {value === 3 && (
            <UserList
              queryHook={useAuthGetAvoidedUsersQuery}
              emptyMessage='You are not avoiding anyone.'
            />
          )}
        </Box>
      </Box>
    </Stack>
  )
}

const Matches = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const { data, isLoading, isError, error, isSuccess } = useEventGetUserMatchesQuery()

  const uniqueMatches = data?.matches?.filter(
    (match, index, self) =>
      index === self.findIndex((m) => m?.user_matched_with?.id === match?.user_matched_with?.id),
  )

  return (
    <Stack spacing={2}>
      {isLoading ? (
        <Skeleton height={80} variant='rectangular' sx={{ borderRadius: '20px' }} />
      ) : isError ? (
        <Alert severity='error' variant='outlined'>
          {errorHandler(error)}
        </Alert>
      ) : isSuccess ? (
        uniqueMatches?.length === 0 ? (
          <Typography textAlign='center' variant='body2' color='text.secondary'>
            You have no matches.
          </Typography>
        ) : (
          uniqueMatches?.map((match) => (
            <CardWrapper
              active={pathname.includes(String(match?.user_matched_with?.id))}
              fullWidth
              sx={{
                cursor: 'pointer',
              }}
              key={match?.match_id}
            >
              <CardActionArea
                sx={{ p: 2 }}
                onClick={() => {
                  navigate(`${COMMUNITY_ROUTE}/${match?.user_matched_with?.id}`)
                }}
              >
                <UserCard user={match?.user_matched_with as BriefPublicUserProfile} />
              </CardActionArea>
            </CardWrapper>
          ))
        )
      ) : null}
    </Stack>
  )
}

const UserList = ({ queryHook, emptyMessage }: { queryHook: any; emptyMessage: string }) => {
  const { user } = useUser()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { data, isLoading, isError, error, isSuccess } = queryHook({
    userId: user?.id as number,
  })
  return (
    <Stack spacing={2}>
      {isLoading ? (
        <Skeleton height={80} variant='rectangular' sx={{ borderRadius: '20px' }} />
      ) : isError ? (
        <Alert severity='error' variant='outlined'>
          {errorHandler(error)}
        </Alert>
      ) : isSuccess ? (
        data?.length === 0 ? (
          <Typography textAlign='center' variant='body2' color='text.secondary'>
            {emptyMessage}
          </Typography>
        ) : (
          data.map((user: BriefPublicUserProfile) => (
            <CardWrapper
              active={pathname.includes(user?.id?.toString())}
              fullWidth
              sx={{
                cursor: 'pointer',
              }}
              key={user?.id}
            >
              <CardActionArea
                sx={{ p: 2 }}
                onClick={() => {
                  navigate(`${COMMUNITY_ROUTE}/${user?.id?.toString()}`)
                }}
              >
                <UserCard user={user} />
              </CardActionArea>
            </CardWrapper>
          ))
        )
      ) : null}
    </Stack>
  )
}

const UserCard = ({ user }: { user: BriefPublicUserProfile }) => {
  return (
    <Stack direction='row' spacing={2} alignItems='center'>
      <Avatar src={user.image || ''} alt={user.username || ''} />
      <Typography>{user?.display_name ? user?.display_name : user?.username || ''}</Typography>
    </Stack>
  )
}

export default CommunityView
